import React from 'react';
import { observer } from 'mobx-react';
import { SettingsBillingWrapper } from '@seedlang/hoc';
import { AppUI } from '@seedlang/state';
import { isPresent } from '@seedlang/utils';
import styled from '@emotion/styled';
import Text from 'components/text';
import Button from 'components/button/button';
import { Theme } from '@seedlang/constants';
import OutlineButton from 'components/button/outline_button';
import SettingsBillingMigrationSection from './settings_billing_migration_section';
import Spinner from 'components/spinner';
import SettingsBillingScheduledSubscriptionsSection from './settings_billing_scheduled_subscriptions_section';
import SettingsBillingPaymentHistorySection from './settings_billing_payment_history_section';
import autobind from 'autobind-decorator';
import { flexLeftColumn } from '@seedlang/style_mixins';
import SeedlangOnly from 'utils/seedlang_only';
import WhitelabelOnly from 'utils/whitelabel_only';
import { computed } from 'mobx';
import PageTitle from 'components/common/page_title';
import SettingsPatreon from './settings_patreon';

const Wrapper = styled.div`
  ${flexLeftColumn()};
  align-items: stretch;
  gap: 20px;

  table {
    width: 100%;
    th {
      text-align: left;
      background:rgb(160, 144, 144);
      color: white;
      font-size: 14px;
      padding: 5px;
    }
    td {
      font-size: 16px;
      padding: 5px;
      i {
        margin-top: 4px;
      }
      &:first-of-type {
        width: 85%;
      }
    }
  }
`;

const Plan = styled.div`
  font-weight: bold;
`;

const Date = styled.div`
  font-size: 14px;
`;

const Cancelled = styled.span`
  color: red;
`;

const TableWrapper = styled.div`
`;

const ButtonRow = styled.div`
  display: flex;
  column-gap: 20px;
  row-gap: 5px;
  @media (max-width: 1150px) {
    flex-direction: column;
  }
`;

@observer
class SettingsBilling extends React.Component {

  @autobind async onManagePaymentMethod() {
    window.location.href = await AppUI.stripeStore.getCustomerPortalUrl({ returnUrl: window.location.href });
  }

  @computed get title() {
    return AppUI.siteIsDefault ? 'Manage Subscription' : 'Your Membership On Our Platform';
  }

  render() {

    if (!AppUI.userIsLoaded) {
      return (
        <Wrapper>
          <PageTitle>{this.title}</PageTitle>
          <Text heading='3'>{this.title}</Text>
          <Spinner />
        </Wrapper>
      );
    }

    return (
      <Wrapper>
        <PageTitle>{this.title}</PageTitle>
        <SeedlangOnly>
          <Text heading='3'>Manage Subscription</Text>
        </SeedlangOnly>
        <WhitelabelOnly>
          <Text heading='2'>Manage Membership</Text>
          <Text heading='3'>Your Membership On Our Platform</Text>
        </WhitelabelOnly>
        {
          AppUI.user.currentUserGroup?.permanent &&
            <div>
              You currently have a free <strong>{AppUI.user.currentGroupMembershipType?.name}</strong>
              {AppUI.user.currentUserGroup.permanentUntil?.date
                ? ` until ${AppUI.user.currentUserGroup.permanentUntil?.formattedDateWithYear}`
                : '.'}
            </div>
        }
        <SeedlangOnly>
          {
            (AppUI.user.permanentMembership && !AppUI.user.mobilePaymentWithoutReceipt) &&
              <Text color={Theme.green} bold>
                You have a free membership. Congrats!
              </Text>
          }
          {
            (AppUI.user.temporaryFreeMembership && !AppUI.user.mobilePaymentWithoutReceipt) &&
              <Text color={Theme.green} bold>
                Congrats, you have a free membership! It will expire on {AppUI.user.memberUntil.formattedDateWithYear}.
              </Text>
          }
          {
            AppUI.user.mobilePaymentWithoutReceipt &&
              <Text color={Theme.green} bold>
                Congrats, you have a membership!
              </Text>
        }
        </SeedlangOnly>
        {
          AppUI.user.isMember && this.props.activeSubscription &&
          <>
            {
              !this.props.activeSubscription.oneTimePayment && isPresent(this.props.activeSubscription.currentPeriodEnd?.formattedDateWithYear) &&
                <Text bold>
                  Your {this.props.activeSubscription?.membership?.title} is active and will {this.props.activeSubscription.cancelAtPeriodEnd ? 'end' : 'renew'} on {this.props.activeSubscription.currentPeriodEnd?.formattedDateWithYear}.
                </Text>
            }
            {
              this.props.activeSubscription.oneTimePayment &&
                <Text bold>
                  You have a lifetime membership. You are awesome! 🎉🎉🎉
                </Text>
            }
            {
              this.props.activeSubscription.pending && !this.props.activeSubscription.oneTimePayment &&
                <Text bold>
                  ⚠️ Please note that PayPal takes 24 hours to send the initial payment. If you wish to cancel your subscription, please do so after the 24 hours has passed.
                </Text>
            }
          </>
        }
        {
          isPresent(this.props.userSubscriptions) &&
            <TableWrapper>
              <table className='table-wrapper credit-cards'>
                <thead>
                  <tr>
                    <th>Plan</th>
                  </tr>
                </thead>
                <tbody>
                  {
                    this.props.userSubscriptions.map(item => {
                      return (
                        <tr key={item.id}>
                          <td>
                            <Plan>
                              {item.membership?.title?.replace(/<[^>]*>/g, '')}
                            </Plan>
                            <Date>
                              {item.currentPeriodStart.formattedDateWithYear} - {item.pending ? 'Pending' : item.currentPeriodEnd.formattedDateWithYear}
                              {
                                item.cancelAtPeriodEnd &&
                                <Cancelled> (Cancelled)</Cancelled>
                              }
                            </Date>
                            {
                              item.pendng && isPresent(item.trialEnd) &&
                                <span className='trial-end'>
                                  {`(Not billed until ${item.trialEnd.formattedDateWithYear})`}
                                </span>
                            }
                            {
                              !item.cancelAtPeriodEnd && item.pending &&
                                <div style={{ fontSize: 12 }}>
                                  Pending Paypal transfer
                                </div>
                            }
                            {
                              !item.cancelAtPeriodEnd && item.pending &&
                                <div style={{ fontSize: 12 }}>
                                  It can take 24 hours for Paypal to transfer the initial payment.
                                </div>
                            }
                          </td>
                        </tr>
                      );
                    })
                  }
                </tbody>
              </table>
            </TableWrapper>
        }
        {
          !AppUI.user.isMember &&
            <Text bold>You don't have an active subscription.</Text>
        }
        <ButtonRow>
          {
            (!AppUI.isPaymentMigrationInProgress && !AppUI.isPaymentMigrationStartable) && !this.props.activeSubscription?.oneTimePayment &&
              <Button
                to={{ name: 'memberships.index' }}
                background={AppUI.site.accentColor || Theme.blue}
              >
                {AppUI.siteIsDefault ? 'Update your Subscription' : 'Update Your Membership Tier'}
              </Button>
          }
          {
            this.props.userSubscriptions.some(sub => sub.paymentMethod === 'stripe') &&
              <Button
                background={AppUI.site.accentColor || Theme.blue}
                onClick={this.onManagePaymentMethod}
              >
                Manage Payment Method
              </Button>
          }
          {
            this.props.showCancelButton && !this.props.activeSubscriptionIsMobile &&
              <OutlineButton
                selectedBackground={Theme.red}
                to='settings.cancel'
              >
                <i className='fa fa-times'
                  style={{ marginRight: 4 }}
                />
                Cancel Membership
              </OutlineButton>
          }
        </ButtonRow>
        {
          AppUI.paymentMigrationEnabled &&
            <SettingsBillingMigrationSection hasActiveSubscription={this.props.activeSubscription?.membership} />
        }
        <WhitelabelOnly>
          <SettingsBillingScheduledSubscriptionsSection />
          <SettingsBillingPaymentHistorySection />
          {
            AppUI.site.patreonClientId &&
              <SettingsPatreon />
          }
        </WhitelabelOnly>
      </Wrapper>
    );
  }
}

export default SettingsBillingWrapper(SettingsBilling);
